import type { TransactionCurrency } from '@contracts/payment/Currency';
import { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import { useAvailableCurrencies } from '@src/clients/financialSettings/hooks/useAvailableCurrencies';
import { NoCurrencyAvailable } from '@src/clients/financialSettings/NoCurrencyAvailable';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { Stack } from '@src/common/components/Stack';
import type { Control, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type CurrencyFieldValue = FieldValues & {
  [CurrencyField.fieldName]: keyof typeof TransactionCurrency | undefined;
};

interface Props<T extends CurrencyFieldValue> {
  control: Control<T>;
  disabled?: boolean;
}

export const AvailableCurrencyDropdown = <T extends CurrencyFieldValue>({
  control,
  disabled = false,
}: Props<T>) => {
  const { t } = useTranslation();
  const { currencies, isLoading } = useAvailableCurrencies();
  const displayDropdown = isLoading || currencies.length > 0;

  return (
    <>
      {displayDropdown && (
        <FormDropdown
          fieldName={CurrencyField.fieldName}
          control={control as Control<CurrencyFieldValue>}
          disabled={isLoading || disabled}
          label={t('fields.currency')}
          options={currencies.map((currency: TransactionCurrency) => ({
            label: t(`currency.${currency.toLowerCase()}`),
            value: currency.toUpperCase(),
          }))}
        />
      )}
      {currencies.length === 0 && !isLoading && (
        <Stack flex={1}>
          <NoCurrencyAvailable />
        </Stack>
      )}
    </>
  );
};
